
























































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BOverlay } from "bootstrap-vue";
import BaseModal from "./BaseModal.vue";
import CustomSelect from "./Select.vue";
import CustomButton from "./Button.vue";
import SolutionModel, { BulkAssignPlansRequest, BulkAssignPlansResponse } from "@/api/solution.model";
import SolutionCategoryModel from "@/api/solution_category.model";
import CorporateClientModel from "@/api/corporate_client_app.model";
import InsurerPlanModel from "@/api/insurer_plan_app.model";
import { showErrorAlert, showWarningAlert } from "@/helpers";
import { SolutionStatus, translateSolutionStatus } from "@/interfaces/solution";

const BATCH_SIZE = 500; // Processar 500 atribuições por lote
const THROTTLE_LIMIT = 50; // Limite do throttler no backend
const THROTTLE_WINDOW = 60000; // 60 segundos em milissegundos
const BATCH_DELAY = 1500; // Delay de 1.5 segundos entre batches para evitar throttling

@Component({
	components: {
		BaseModal,
		CustomSelect,
		CustomButton,
		BOverlay,
	},
})
export default class BulkAssignPlansModal extends Vue {
	@Prop({ default: false }) value!: boolean;

	// Data properties
	selectedSolutions: any[] = [];
	selectedCorporateClients: any[] = [];
	selectedPlans: any[] = [];
	selectedCategories: any[] = [];
	selectedStatuses: any[] = [];
	solutionOptions: any[] = [];
	categoryOptions: any[] = [];
	statusOptions: any[] = [];
	corporateClientOptions: any[] = [];
	planOptions: any[] = [];
	isLoading = false;
	isProcessing = false;
	result: BulkAssignPlansResponse | null = null;

	// Progress tracking
	currentBatch = 0;
	totalBatches = 0;
	totalProcessed = 0;

	// Rate limit tracking
	requestTimestamps: number[] = [];

	// Computed properties
	get filteredPlanOptions() {
		if (this.selectedCorporateClients.length === 0) {
			return [];
		}

		const selectedClientIds = this.selectedCorporateClients.map((cc: any) => cc.id);

		return this.planOptions.filter((option: any) =>
			selectedClientIds.includes(option.value.insurerPlan.corporateClient.id),
		);
	}

	get previewCount() {
		return this.selectedSolutions.length * this.selectedPlans.length;
	}

	get isExecuteDisabled() {
		return (
			this.selectedSolutions.length === 0 || this.selectedPlans.length === 0 || this.isLoading || this.isProcessing
		);
	}

	get progressPercentage() {
		if (this.totalBatches === 0) return 0;
		return Math.round((this.currentBatch / this.totalBatches) * 100);
	}

	// Lifecycle
	async mounted() {
		await this.loadData();
	}

	// Watchers
	@Watch("value")
	onModalOpen(isOpen: boolean) {
		if (isOpen) {
			this.resetForm();
			this.loadData();
		}
	}

	@Watch("selectedCorporateClients")
	onCorporateClientsChange() {
		// Limpar planos selecionados quando clientes corporativos mudam
		this.selectedPlans = this.selectedPlans.filter((plan: any) => {
			const selectedClientIds = this.selectedCorporateClients.map((cc: any) => cc.id);
			return selectedClientIds.includes(plan.insurerPlan.corporateClient.id);
		});
	}

	@Watch("selectedCategories")
	@Watch("selectedStatuses")
	async onFiltersChange() {
		// Recarregar soluções com os filtros aplicados
		await this.loadSolutions();
		// Limpar soluções selecionadas quando filtros mudam
		this.selectedSolutions = [];
	}

	// Methods
	/**
	 * Verifica se podemos fazer uma requisição sem violar o rate limit
	 * Remove timestamps antigos (fora da janela de 60s) e verifica se atingimos o limite
	 */
	canMakeRequest(): boolean {
		const now = Date.now();
		// Remove timestamps mais antigos que a janela de throttle
		this.requestTimestamps = this.requestTimestamps.filter(timestamp => now - timestamp < THROTTLE_WINDOW);
		return this.requestTimestamps.length < THROTTLE_LIMIT;
	}

	/**
	 * Calcula quanto tempo devemos esperar antes da próxima requisição
	 * Retorna 0 se podemos fazer a requisição imediatamente
	 */
	calculateDelay(): number {
		if (this.canMakeRequest()) {
			return 0;
		}
		// Se atingimos o limite, precisamos esperar até que o timestamp mais antigo saia da janela
		const oldestTimestamp = this.requestTimestamps[0];
		const timeToWait = THROTTLE_WINDOW - (Date.now() - oldestTimestamp);
		return Math.max(0, timeToWait) + 100; // Adiciona 100ms de margem de segurança
	}

	/**
	 * Registra uma requisição no tracking de rate limit
	 */
	registerRequest(): void {
		this.requestTimestamps.push(Date.now());
	}

	/**
	 * Aguarda um delay antes de continuar
	 */
	async sleep(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	async loadSolutions() {
		// Construir filtro para o backend seguindo o padrão de EntityTable
		const searchFilter: any = {};

		// Filtro por categoria - usando category.name como filtro direto
		if (this.selectedCategories && this.selectedCategories.length > 0) {
			// Precisamos buscar os nomes das categorias a partir dos IDs selecionados
			const categoryNames = this.selectedCategories
				.map((categoryId: string) => {
					const category = this.categoryOptions.find((opt: any) => opt.value === categoryId);
					return category ? category.name : null;
				})
				.filter((name: string | null) => name !== null);

			// Adicionar filtro direto por categoria
			if (categoryNames.length === 1) {
				searchFilter["category.name"] = categoryNames[0];
			} else if (categoryNames.length > 1) {
				searchFilter["category.name"] = {
					$in: categoryNames,
				};
			}
		}

		// Filtro por status
		if (this.selectedStatuses && this.selectedStatuses.length > 0) {
			// Adicionar filtro direto por status
			if (this.selectedStatuses.length === 1) {
				searchFilter.status = this.selectedStatuses[0];
			} else {
				searchFilter.status = {
					$in: this.selectedStatuses,
				};
			}
		}

		// Carregar soluções com filtros aplicados
		const searchParams: any = {
			page: 1,
			limit: 0,
		};

		// Adicionar o filtro dentro de 'search'
		if (Object.keys(searchFilter).length > 0) {
			searchParams.search = searchFilter;
		}

		// Adicionar join para trazer os dados da categoria
		searchParams.join = [{ field: "category" }];
		const solutionsResponse = await SolutionModel.search(searchParams);
		const solutionsData = Array.isArray(solutionsResponse) ? solutionsResponse : solutionsResponse.data || [];
		this.solutionOptions = solutionsData.map((solution: any) => ({
			name: solution.name,
			value: {
				id: solution.id,
				name: solution.name,
				category: solution.category,
				status: solution.status,
			},
		}));
	}

	async loadData() {
		this.isLoading = true;
		try {
			// Carregar categorias
			const categoriesResponse = await SolutionCategoryModel.search({});
			const categoriesData = Array.isArray(categoriesResponse) ? categoriesResponse : categoriesResponse.data || [];
			this.categoryOptions = categoriesData.map((category: any) => ({
				name: category.name,
				value: category.id,
			}));

			// Carregar opções de status
			this.statusOptions = Object.values(SolutionStatus).map(status => ({
				name: translateSolutionStatus(status),
				value: status,
			}));

			// Carregar soluções inicialmente sem filtros
			await this.loadSolutions();

			// Carregar clientes corporativos
			const corporateClientsResponse = await CorporateClientModel.search({});
			const corporateClientsData = Array.isArray(corporateClientsResponse)
				? corporateClientsResponse
				: corporateClientsResponse.data || [];
			this.corporateClientOptions = corporateClientsData.map((client: any) => ({
				name: client.name,
				value: { id: client.id, name: client.name },
			}));

			// Carregar todos os planos (serão filtrados por cliente selecionado)
			const plansResponse = await InsurerPlanModel.search({ page: 1, limit: 0 });
			const plansData = Array.isArray(plansResponse) ? plansResponse : plansResponse.data || [];
			this.planOptions = plansData.map((plan: any) => ({
				name: `${plan.name} - ${plan.corporateClient.name}`,
				value: {
					insurerPlan: {
						id: plan.id,
						name: plan.name,
						corporateClient: {
							id: plan.corporateClient.id,
							name: plan.corporateClient.name,
						},
					},
				},
			}));
		} catch (error) {
			console.error("Erro ao carregar dados:", error);
			showErrorAlert("Erro ao carregar dados. Tente novamente.");
		} finally {
			this.isLoading = false;
		}
	}

	async executeAssignment() {
		if (this.isExecuteDisabled) return;

		this.isProcessing = true;
		this.result = null;
		this.totalProcessed = 0;
		this.requestTimestamps = []; // Resetar tracking de rate limit

		try {
			const solutionIds = this.selectedSolutions.map((s: any) => s.id);
			const insurerPlanIds = this.selectedPlans.map((p: any) => p.insurerPlan.id);

			// Dividir em lotes se necessário
			const batches = this.createBatches(solutionIds, insurerPlanIds);
			this.totalBatches = batches.length;
			this.currentBatch = 0;

			// Calcular tempo estimado total
			const estimatedTimeSeconds = Math.ceil((batches.length * BATCH_DELAY) / 1000);

			// Alertar usuário se a operação vai demorar mais de 60 segundos
			if (estimatedTimeSeconds > 60) {
				const minutes = Math.ceil(estimatedTimeSeconds / 60);
				showWarningAlert(
					`Esta operação processará ${batches.length} lotes e pode levar aproximadamente ${minutes} minuto(s). ` +
						`Por favor, não feche esta janela até a conclusão.`,
					"Operação Longa",
				);
			}

			let totalCreated = 0;
			let totalAlreadyExisted = 0;
			let totalCombinations = 0;
			let allSolutionNames: string[] = [];
			let allPlanNames: string[] = [];

			// Processar cada lote sequencialmente com controle de rate limit
			for (const batch of batches) {
				this.currentBatch++;

				// Calcular delay necessário para respeitar o rate limit
				const requiredDelay = this.calculateDelay();

				if (requiredDelay > 0) {
					// Se precisamos esperar, aguardar o tempo necessário
					await this.sleep(requiredDelay);
				} else if (this.currentBatch > 1) {
					// Se não precisamos esperar por rate limit mas não é o primeiro batch,
					// adicionar um delay padrão para evitar sobrecarga
					await this.sleep(BATCH_DELAY);
				}

				// Registrar a requisição no tracking
				this.registerRequest();

				// Fazer a requisição
				const response = await SolutionModel.bulkAssignPlans(batch);

				totalCreated += response.created;
				totalAlreadyExisted += response.alreadyExisted;
				totalCombinations += response.total;
				this.totalProcessed += response.total;

				// Consolidar nomes (evitando duplicatas)
				if (response.solutionNames) {
					response.solutionNames.forEach(name => {
						if (!allSolutionNames.includes(name)) {
							allSolutionNames.push(name);
						}
					});
				}
				if (response.planNames) {
					response.planNames.forEach(name => {
						if (!allPlanNames.includes(name)) {
							allPlanNames.push(name);
						}
					});
				}
			}

			// Consolidar resultado final com nomes
			this.result = {
				created: totalCreated,
				alreadyExisted: totalAlreadyExisted,
				total: totalCombinations,
				message: `${totalCreated} atribuições criadas com sucesso${
					totalAlreadyExisted > 0 ? `, ${totalAlreadyExisted} já existiam` : ""
				}`,
				solutionNames: allSolutionNames,
				planNames: allPlanNames,
			};

			// Emitir evento de sucesso para recarregar tabela de soluções
			this.$emit("success");
		} catch (error: any) {
			console.error("Erro ao executar atribuição em lote:", error);
			showErrorAlert(error?.response?.message || "Erro ao executar atribuição. Tente novamente.");
		} finally {
			this.isProcessing = false;
		}
	}

	createBatches(solutionIds: string[], insurerPlanIds: string[]): BulkAssignPlansRequest[] {
		const batches: BulkAssignPlansRequest[] = [];
		const totalCombinations = solutionIds.length * insurerPlanIds.length;

		if (totalCombinations <= BATCH_SIZE) {
			// Se cabe em um único lote, enviar tudo de uma vez
			batches.push({ solutionIds, insurerPlanIds });
		} else {
			// Dividir em lotes menores
			// Estratégia: fixar insurerPlanIds e dividir solutionIds
			const plansPerBatch = Math.ceil(BATCH_SIZE / insurerPlanIds.length);

			for (let i = 0; i < solutionIds.length; i += plansPerBatch) {
				const batchSolutionIds = solutionIds.slice(i, i + plansPerBatch);
				batches.push({
					solutionIds: batchSolutionIds,
					insurerPlanIds,
				});
			}
		}

		return batches;
	}

	resetForm() {
		this.selectedSolutions = [];
		this.selectedCorporateClients = [];
		this.selectedPlans = [];
		this.selectedCategories = [];
		this.selectedStatuses = [];
		this.result = null;
		this.currentBatch = 0;
		this.totalBatches = 0;
		this.totalProcessed = 0;
	}

	closeModal() {
		this.$emit("input", false);
	}
}
